import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Button } from './ui/Button';
import toast from 'react-hot-toast';
import { ASSESSMENT_LABEL_TO_KEY, ASSESSMENT_OPTIONS } from '../utils/assessmentUtils';

const ROLE_VALUES = new Set(['USER', 'CUSTOMER_ADMIN', 'HR']);

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const normalizeRole = (roleValue) =>
  String(roleValue || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');

const normalizeAssessmentKey = (assessmentLabel) => {
  const key = ASSESSMENT_LABEL_TO_KEY[String(assessmentLabel || '').trim().toLowerCase()];
  return key || null;
};

const mapRoleToPayload = (roleValue) => {
  if (roleValue === 'USER') return 'user';
  if (roleValue === 'CUSTOMER_ADMIN') return 'customer_admin';
  if (roleValue === 'HR') return 'hr';
  return '';
};

const BulkUploadModal = ({ isOpen, onClose, onSubmit, loading }) => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [validPayloadRows, setValidPayloadRows] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [parsing, setParsing] = useState(false);
  const fileInputRef = useRef(null);

  const resetModal = () => {
    setFile(null);
    setDragActive(false);
    setPreviewRows([]);
    setValidPayloadRows([]);
    setUploadResult(null);
    setParsing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const validateAndSetFile = (selectedFile) => {
    if (selectedFile.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      toast.error('Please select a valid .xlsx file');
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setFile(selectedFile);
    setPreviewRows([]);
    setValidPayloadRows([]);
    setUploadResult(null);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) validateAndSetFile(droppedFile);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) validateAndSetFile(selectedFile);
  };

  const parseFileForPreview = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setParsing(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      if (!rawRows.length) {
        toast.error('Excel file is empty');
        setPreviewRows([]);
        setValidPayloadRows([]);
        return;
      }

      const emailCounts = new Map();
      const intermediateRows = rawRows.map((row, index) => {
        const serial = row['S.No'] || index + 1;
        const name = String(row.Name || '').trim();
        const email = String(row.Email || '').trim().toLowerCase();
        const phone = String(row['Phone No'] || '').trim();
        const roleRaw = normalizeRole(row.Role || '');
        const assessmentType = String(row['Assessment Type'] || '').trim();
        const assessmentKey = normalizeAssessmentKey(assessmentType);

        if (email) emailCounts.set(email, (emailCounts.get(email) || 0) + 1);

        return {
          serial,
          name,
          email,
          phone,
          roleRaw,
          assessmentType,
          assessmentKey,
          status: '',
          isValid: true,
          payload: null
        };
      });

      const processedRows = intermediateRows.map((row) => {
        if (!row.name) {
          return { ...row, isValid: false, status: 'Missing required field: Name' };
        }
        if (!row.email) {
          return { ...row, isValid: false, status: 'Missing required field: Email' };
        }
        if (!row.phone) {
          return { ...row, isValid: false, status: 'Missing required field: Phone' };
        }
        if (!validateEmail(row.email)) {
          return { ...row, isValid: false, status: 'Invalid email format' };
        }
        if (!ROLE_VALUES.has(row.roleRaw)) {
          return {
            ...row,
            isValid: false,
            status: 'Invalid role. Must be USER, CUSTOMER_ADMIN, or HR'
          };
        }
        if (row.roleRaw === 'USER' && !row.assessmentType) {
          return { ...row, isValid: false, status: 'Assessment Type required for USER role' };
        }
        if (row.roleRaw === 'USER' && !row.assessmentKey) {
          return { ...row, isValid: false, status: 'Invalid assessment type' };
        }
        if (row.assessmentType && !row.assessmentKey) {
          return { ...row, isValid: false, status: 'Invalid assessment type' };
        }
        if ((emailCounts.get(row.email) || 0) > 1) {
          return { ...row, isValid: false, status: 'Duplicate email in file' };
        }

        const payload = {
          name: row.name,
          email: row.email,
          phone: row.phone,
          role: mapRoleToPayload(row.roleRaw)
        };
        if (row.roleRaw === 'USER') {
          payload.assessment_key = row.assessmentKey;
        }

        return { ...row, status: 'Valid', payload };
      });

      const validPayloads = processedRows.filter((row) => row.isValid && row.payload).map((row) => row.payload);
      setPreviewRows(processedRows);
      setValidPayloadRows(validPayloads);
    } catch (error) {
      console.error('Bulk parse failed:', error);
      toast.error('Failed to parse Excel file');
      setPreviewRows([]);
      setValidPayloadRows([]);
    } finally {
      setParsing(false);
    }
  };

  const handleProceedWithValidRows = async () => {
    if (!validPayloadRows.length) {
      toast.error('No valid rows to submit');
      return;
    }

    try {
      const result = await onSubmit(validPayloadRows);
      const successCount = Array.isArray(result?.success) ? result.success.length : 0;
      const failedRows = Array.isArray(result?.failed) ? result.failed : [];
      const failedCount = failedRows.length;

      setUploadResult({
        successCount,
        failedCount,
        failures: failedRows
      });

      if (successCount > 0 || failedCount > 0) {
        toast.success(`${successCount} users invited successfully | ${failedCount} failed`);
      }
    } catch (error) {
      console.error('Bulk assignment failed:', error);
      toast.error(error.response?.data?.error || 'Failed to assign assessments');
    }
  };

  const downloadTemplate = () => {
    const headers = ['S.No', 'Name', 'Email', 'Phone No', 'Role', 'Assessment Type'];
    const rows = [
      headers,
      ['1', 'John Doe', 'john@example.com', '+919999999999', 'USER', 'Aptitude'],
      ['2', 'Jane HR', 'jane.hr@example.com', '+919999999998', 'HR', '']
    ];

    const templateSheet = XLSX.utils.aoa_to_sheet(rows);
    templateSheet['!cols'] = [
      { wch: 8 },
      { wch: 24 },
      { wch: 32 },
      { wch: 18 },
      { wch: 18 },
      { wch: 30 }
    ];

    templateSheet['!dataValidation'] = [
      {
        sqref: 'E2:E500',
        type: 'list',
        allowBlank: false,
        formula1: '"USER,CUSTOMER_ADMIN,HR"'
      },
      {
        sqref: 'F2:F500',
        type: 'list',
        allowBlank: true,
        formula1: `"${ASSESSMENT_OPTIONS.map((option) => option.label).join(',')}"`
      }
    ];

    const instructionsRows = [
      ['Column', 'Required', 'Valid Values / Notes'],
      ['S.No', 'Yes', 'Sequential row number'],
      ['Name', 'Yes', 'Full name of user'],
      ['Email', 'Yes', 'Must be a valid and unique email'],
      ['Phone No', 'Yes', 'E.164 format, e.g. +919999999999'],
      ['Role', 'Yes', 'USER, CUSTOMER_ADMIN, HR'],
      ['Assessment Type', 'Conditional', 'Required only when Role = USER'],
      ['Supported Assessment Types', 'Info', ASSESSMENT_OPTIONS.map((option) => option.label).join(' | ')]
    ];
    const instructionSheet = XLSX.utils.aoa_to_sheet(instructionsRows);
    instructionSheet['!cols'] = [{ wch: 26 }, { wch: 14 }, { wch: 80 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, templateSheet, 'Template');
    XLSX.utils.book_append_sheet(workbook, instructionSheet, 'Instructions');
    XLSX.writeFile(workbook, 'user_bulk_upload_template.xlsx');
  };

  const validCount = previewRows.filter((row) => row.isValid).length;
  const errorCount = previewRows.length - validCount;

  return (
    <AnimatePresence>
      {isOpen ? (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={handleClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Bulk User Upload</h2>
                  <p className="text-sm text-gray-600 mt-1">Upload multiple users from Excel file</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleClose} className="p-2">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="p-6 space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-900">Required Template Format</h4>
                      <p className="text-sm text-blue-800 mt-1">
                        Your Excel file must have these columns: S.No, Name, Email, Phone No, Role, Assessment Type
                      </p>
                      <div className="mt-3">
                        <Button variant="outline" size="sm" onClick={downloadTemplate} className="flex items-center space-x-2">
                          <Download className="w-4 h-4" />
                          <span>Download Template</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive
                      ? 'border-blue-500 bg-blue-50'
                      : file
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input ref={fileInputRef} type="file" accept=".xlsx" onChange={handleFileSelect} className="hidden" />

                  {file ? (
                    <div className="space-y-2">
                      <CheckCircle className="w-12 h-12 text-green-600 mx-auto" />
                      <p className="text-sm font-medium text-green-900">{file.name}</p>
                      <p className="text-xs text-green-700">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      <Button variant="outline" size="sm" onClick={() => setFile(null)} className="mt-2">
                        Remove File
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                      <p className="text-sm font-medium text-gray-900">
                        Drop your Excel file here, or{' '}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          browse
                        </button>
                      </p>
                      <p className="text-xs text-gray-500">Only .xlsx files up to 5MB</p>
                    </div>
                  )}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-yellow-900 mb-2">Important Notes</h4>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    <li>• Role must be one of: USER, CUSTOMER_ADMIN, HR</li>
                    <li>• Assessment Type is required when Role is USER</li>
                    <li>
                      • Supported Assessment Types: {ASSESSMENT_OPTIONS.map((option) => option.label).join(', ')}
                    </li>
                    <li>• Phone numbers must be in E.164 format</li>
                    <li>• Email addresses must be unique</li>
                    <li>• All users will receive invitation emails with temporary passwords</li>
                  </ul>
                </div>

                {previewRows.length ? (
                  <div className="space-y-4">
                    <div className="text-sm font-medium text-gray-700">
                      {validCount} valid rows | {errorCount} rows with errors
                    </div>

                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-3 py-2 text-left">S.No</th>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Email</th>
                            <th className="px-3 py-2 text-left">Phone</th>
                            <th className="px-3 py-2 text-left">Role</th>
                            <th className="px-3 py-2 text-left">Assessment Type</th>
                            <th className="px-3 py-2 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewRows.map((row, idx) => (
                            <tr key={`${row.serial}-${idx}`} className={row.isValid ? 'bg-green-50' : 'bg-red-50'}>
                              <td className="px-3 py-2">{row.serial}</td>
                              <td className="px-3 py-2">{row.name}</td>
                              <td className="px-3 py-2">{row.email}</td>
                              <td className="px-3 py-2">{row.phone}</td>
                              <td className="px-3 py-2">{row.roleRaw}</td>
                              <td className="px-3 py-2">{row.assessmentType || '-'}</td>
                              <td className={`px-3 py-2 ${row.isValid ? 'text-green-700' : 'text-red-700'}`}>{row.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex gap-3">
                      <Button onClick={handleProceedWithValidRows} loading={loading} disabled={!validCount}>
                        Proceed with valid rows only
                      </Button>
                      <Button variant="outline" onClick={handleClose} disabled={loading}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}

                {uploadResult ? (
                  <div className="space-y-3">
                    <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-800">
                      {uploadResult.successCount} users invited successfully | {uploadResult.failedCount} failed
                    </div>
                    {uploadResult.failures.length ? (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start space-x-2">
                          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                          <div className="space-y-1">
                            {uploadResult.failures.map((failure, index) => (
                              <div key={`failure-${index}`} className="text-xs text-red-800">
                                Row {Number(failure.index) + 1 || '-'} ({failure.email || 'N/A'}): {failure.reason}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="border-t border-gray-200 p-6 flex justify-end gap-3">
                <Button variant="outline" onClick={handleClose} disabled={loading || parsing}>
                  {uploadResult ? 'Close' : 'Cancel'}
                </Button>
                {!previewRows.length ? (
                  <Button onClick={parseFileForPreview} loading={parsing} disabled={!file || loading}>
                    Parse and Preview
                  </Button>
                ) : null}
              </div>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
};

export default BulkUploadModal;
